import {
  CreateDataBlockDto,
  DataBlockDto,
  DeviceModel,
  FileInfoDto,
  FileParameter,
  FileType,
  MeasurementImportSettingsDto,
  QueryFactory,
  UpdateDataBlockDto,
} from '@lmt/shared-types';

import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { uploadMeasurementFile } from './file-operations';
import { createMeasurement, uploadFile } from './operations';

export interface ImportData {
  patientId: number;
  caseId: number;
  visitId?: number;
  deviceModel: DeviceModel;
  typeId: number;
  values: { [key: string]: string | number };
  sdbdataFilePath: string;
  pdfFilePath?: string;
  pdfFile?: FileParameter;
  pdfFileName: string;
  startDate: Date;
  endDate: Date;
  file?: FileParameter;
  replacedMeasurement?: DataBlockDto | null;
  isStatisticCompleted: boolean;
  valuesHash?: string;
  settings: MeasurementImportSettingsDto;
}

async function importMeasurementFiles(
  measurementId: number,
  data: ImportData,
  existingPdfFile?: FileInfoDto,
) {
  const comment = `DeviceModel: ${data.deviceModel}, Type: ${data.typeId}`;

  if (!!data.file) {
    await uploadFile(
      data.file,
      FileType.Sdbdata,
      comment,
      data.patientId,
      measurementId,
    );
  }

  await importPdfFile(measurementId, data, comment, existingPdfFile);
}

async function importPdfFile(
  measurementId: number,
  data: ImportData,
  comment: string,
  existingFile?: FileInfoDto, // if set we import file as new version of existing file
) {
  if (data.pdfFile) {
    const pdfFile: FileParameter = {
      data: data.pdfFile.data,
      fileName: data.pdfFileName,
    };
    if (existingFile) {
      const newVersion = await QueryFactory.FileClient.updateFile(
        existingFile.id,
        pdfFile,
      );
      if (!!newVersion.values?.['IsOutdated']) {
        // remove flag 'outdated'
        await QueryFactory.FileClient.patchFile(newVersion.id, {
          values: {
            IsOutdated: false,
          },
        });
      }
      return;
    }
    await uploadFile(
      pdfFile,
      FileType.DeviceReport,
      comment,
      data.patientId,
      measurementId,
      data.startDate,
    );
  } else if (data.pdfFilePath && isElectron()) {
    const filePath = data.pdfFilePath;
    if (isElectron()) {
      const buffer = await uploadMeasurementFile(filePath);
      if (buffer) {
        const filename = filePath.replace(/^.*[\\\/]/, '');
        const fileBlob = new Blob([buffer], {
          type: filePath.split('.').pop(),
        });
        const file = new File([fileBlob], filename);
        const fileParameter: FileParameter = {
          fileName: data.pdfFileName,
          data: file,
        };
        if (file) {
          if (existingFile) {
            const newVersion = await QueryFactory.FileClient.updateFile(
              existingFile.id,
              fileParameter,
            );
            if (!!newVersion.values?.['IsOutdated']) {
              // remove flag 'outdated'
              await QueryFactory.FileClient.patchFile(newVersion.id, {
                values: {
                  IsOutdated: false,
                },
              });
            }
            return;
          }
          await uploadFile(
            fileParameter,
            FileType.DeviceReport,
            comment,
            data.patientId,
            measurementId,
            data.startDate,
          );
        }
      }
    }
  }
}

export async function importMeasurement(data: ImportData): Promise<number> {
  console.log('importMeasurement started');

  if (data.replacedMeasurement) {
    const measurementFiles = await QueryFactory.FileClient.searchFiles(
      data.patientId,
      data.replacedMeasurement.id,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
    );
    const existingReportFile = measurementFiles.find(
      (x) => x.name === data.pdfFileName && x.type === FileType.DeviceReport,
    );

    // if values are the same, we only need to add pdf report
    if (
      data.valuesHash &&
      data.replacedMeasurement.valuesHash === data.valuesHash
    ) {
      if (
        existingReportFile &&
        !data.settings.saveOldFilesWhenMeasurementReplaced
      ) {
        // if report file is the same (and 'Save old files' is disabled):
        // - if file is outdated we should delete it and import new one
        // - otherwise there is nothing to do: values and report are up-to-date
        if (!!existingReportFile.values?.['IsOutdated']) {
          await QueryFactory.FileClient.deleteFile(existingReportFile.groupId);
        } else {
          return data.replacedMeasurement.id;
        }
      }

      const comment = `DeviceModel: ${data.deviceModel}, Type: ${data.typeId}`;
      const isExistingFileActual =
        !!existingReportFile &&
        (data.settings.saveOldFilesWhenMeasurementReplaced ||
          !existingReportFile.values?.['IsOutdated']);
      await importPdfFile(
        data.replacedMeasurement.id,
        data,
        comment,
        isExistingFileActual ? existingReportFile : undefined,
      );
      return data.replacedMeasurement.id;
    }

    // otherwise update existing measurement with new values
    const dto: UpdateDataBlockDto = {
      values: data.values,
      valuesHash: data.valuesHash,
      isStatisticCompleted: data.isStatisticCompleted,
    };
    await QueryFactory.DataBlockClient.edit(data.replacedMeasurement.id, dto);
    const replacedMeasurementFile = measurementFiles.find(
      (x) => x.type === FileType.Sdbdata,
    );
    if (replacedMeasurementFile) {
      await QueryFactory.FileClient.deleteFile(replacedMeasurementFile.groupId);
    }

    const replacedMeasurementReportFiles = measurementFiles.filter(
      (x) => x.type === FileType.DeviceReport,
    );
    if (!data.settings.saveOldFilesWhenMeasurementReplaced) {
      // remove old device report files
      for (const file of replacedMeasurementReportFiles) {
        await QueryFactory.FileClient.deleteFile(file.groupId);
      }
      // import new file
      await importMeasurementFiles(data.replacedMeasurement.id, data);
      return data.replacedMeasurement.id;
    } else {
      // otherwise mark all device report files as outdated
      for (const file of replacedMeasurementReportFiles) {
        await QueryFactory.FileClient.patchFile(file.id, {
          values: {
            IsOutdated: true,
          },
        });
      }
      // if report file exists new file will be imported as version of the existing file
      await importMeasurementFiles(
        data.replacedMeasurement.id,
        data,
        existingReportFile,
      );
      return data.replacedMeasurement.id;
    }
  } else {
    const measurement: CreateDataBlockDto = {
      begin: data.startDate,
      end: data.endDate,
      visitId: data.visitId,
      caseId: data.caseId,
      patientId: data.patientId,
      deviceModel: data.deviceModel,
      typeId: data.typeId,
      values: data.values,
      isStatisticCompleted: data.isStatisticCompleted,
      valuesHash: data.valuesHash,
    };
    const measurementId = await createMeasurement(measurement);

    await importMeasurementFiles(measurementId, data);

    return measurementId;
  }
}
